import { getInitials } from '../../utils/data/get-initials';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { LoanExtension as LoanModel } from 'tio-data/data/resources/loan.ext';
import type { Person as PersonModel } from './person.type';

import type { Person } from './person.type.ts';
export interface PersonExtension extends Person {}
export class PersonExtension {
  // ── Computed Properties ──
  get fullName() {
    return [this.firstName, this.lastName].join(' ');
  }

  get initials(): string {
    return getInitials(this as unknown as PersonModel);
  }

  get primaryEmail() {
    return this.emailAddresses.find((e) => e.contactType === 'PRIMARY');
  }

  /** Their mobile, if one is still in service — a removed row keeps answering otherwise. */
  get primaryPhoneNumber() {
    return this.phoneNumbers.find((phone) => phone.phoneNumberType === 'MOBILE' && phone.isActive);
  }

  get accountStats() {
    const totalDebt = this.totalLoanBalance;
    let totalLoans = 0;

    // TODO: Somehow just do all the loops once.
    this.activeAccounts.forEach((account) => {
      totalLoans += account.activeLoans.length;
    });

    return {
      totalDebt,
      totalLoans,
    };
  }

  get accountReceivingContributions(): AccountModel | null {
    return (
      this.activeAccounts.find(({ id }) => id === this.accountReceivingContributionsId) || null
    );
  }

  get activeAccounts() {
    return (this.accounts?.filter((e) => !e.deactivatedOn) || [])
      .filter((account) => !account.observation?.['is-paid-off'])
      .sort((a, b) => a.publicOrdinalValue - b.publicOrdinalValue);
  }

  get paidAccounts() {
    return (this.accounts?.filter((e) => !e.deactivatedOn) || []).filter(
      (account) => account.observation?.['is-paid-off'] === true
    );
  }

  get inactiveAccounts() {
    return this.accounts?.filter((e) => !!e.deactivatedOn) || [];
  }

  get totalLoanBalance() {
    let balance = 0;

    this.activeAccounts.forEach(
      (account) => (balance += account.deactivatedOn ? 0 : account.loanTotal)
    );
    return balance;
  }

  get weightedInterestRate() {
    return (
      (
        this.activeAccounts.reduce(
          // parseFloat coerces its argument to a string at runtime; make that
          // explicit since loanInterestAverage is typed as a number.
          (a, { loanInterestAverage: b }) => a + parseFloat(String(b || 0.0)),
          0.0
        ) / this.activeAccounts.length
      ).toFixed(2) || '0.00'
    );
  }

  get allActiveAccountLoans() {
    return this.activeAccounts
      .reduce((allLoans: LoanModel[], account) => {
        const loans = account.activeLoans.slice();
        return allLoans.concat(loans);
      }, [])
      .flat();
  }

  get needsToLinkLoans() {
    return this.activeAccounts.length === 0;
  }

  get reasonForNotLinkingLoans() {
    return this.preferences['reason-for-not-linking-loans'] as string;
  }

  get needsToAnswerWhyNoLinkedLoans() {
    return !!(this.needsToLinkLoans && !this.reasonForNotLinkingLoans);
  }

  get activeEmployee() {
    return this.employees.find((employee) => employee.isActive);
  }

  get invites() {
    return this.employees
      .filter((employee) => employee.isActive)
      .map((employee) => employee.invites)
      .flat();
  }
}

const Registration = {
  kind: 'object',
  name: 'person',
  features: PersonExtension,
};
export default Registration;
